/**
 * Super-admin billing domain - built on apiClient via useRequest, not
 * useAppServices(). Two distinct legacy Stripe surfaces live here side by
 * side, each wired to its own real backend route (preserved as-is, not
 * merged - see each hook's comment):
 *   - billing/index.js, billing/components/Invoices/index.js use the older
 *     /agency/stripe/filter/:id and /agency/stripe/invoices/:id routes.
 *   - billing/components/PaymentMethod/index.js's AddPaymentMethod form
 *     posts to /agency/stripe/submit and /agency/stripe/update/:id.
 *   - settings/billing/components/Balance/index.js reads the agency's
 *     AI-wallet card off /agency/stripe (agency_id query) - same
 *     "agency-stripe" resource name components/Subscriptionodal/AddCardModal
 *     (queries/subscriptions.js's useAgencyStripe) invalidates after
 *     creating/updating that card, so both stay in sync.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";

const { GET, POST } = ReqMethods;

export const billingKeys = {
  stripeFilter: (agencyId) => ["stripe-filter", agencyId],
  invoices: (agencyId) => ["stripe-invoices", agencyId],
  agencyStripeCard: (agencyId) => ["agency-stripe", agencyId],
};

export function useAgencyStripeFilter(agencyId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: billingKeys.stripeFilter(agencyId),
    queryFn: async () => {
      const { response } = await request(GET, `/agency/stripe/filter/${agencyId}`);
      return response?.data?.stripe_data || "";
    },
    enabled: !!agencyId,
    ...options,
  });
}

export function useAgencyStripeInvoices(agencyId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: billingKeys.invoices(agencyId),
    queryFn: async () => {
      const { response } = await request(GET, `/agency/stripe/invoices/${agencyId}`);
      return response?.data?.data || [];
    },
    enabled: !!agencyId,
    ...options,
  });
}

export function useAgencyStripeCard(agencyId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: billingKeys.agencyStripeCard(agencyId),
    queryFn: async () => {
      const { response, error } = await request(GET, "/agency/stripe", {
        query: `agency_id=${agencyId}`,
      });
      if (error) throw error;
      return response.data;
    },
    enabled: !!agencyId,
    ...options,
  });
}

export function useCreateStripePaymentMethod() {
  const request = useRequest();
  return useMutation({
    mutationFn: (payload) => request(POST, "/agency/stripe/submit", { payload }),
  });
}

export function useUpdateStripePaymentMethod() {
  const request = useRequest();
  return useMutation({
    mutationFn: ({ payload, id }) => request(POST, `/agency/stripe/update/${id}`, { payload }),
  });
}
